import { AllowedPostMediaTypes } from "@src/gql/generated";
import { BASEURL } from "@src/utils/api";
import { MediaModule } from "@src/utils/mediaModule";
import { uploadLargeFileInChunks } from "@src/utils/helpers/upload-post/multipartUpload";
import { withResolvedMimeType } from "@src/utils/helpers/resolveUploadMimeType";

export type UploadResult = {
  id: string | null;
  fileUrl: string | null;
  isMediaUpload?: boolean;
};

export const MULTIPART_UPLOAD_THRESHOLD_BYTES = 20 * 1024 * 1024;

function isDocumentMimeType(file: File): boolean {
  const allowedDocTypes = [
    "application/pdf",
    "application/msword",
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
    "application/vnd.ms-excel",
    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    "application/vnd.ms-powerpoint",
    "application/vnd.openxmlformats-officedocument.presentationml.presentation",
  ];
  return allowedDocTypes.includes(file.type);
}

function getMediaTypeByExtension(
  file: File,
  isDocumentUpload?: boolean,
): AllowedPostMediaTypes {
  if (isDocumentUpload) {
    if (isDocumentMimeType(file)) {
      return AllowedPostMediaTypes.Document;
    }
    throw new Error(
      "Only PDF, DOC, DOCX, XLS, XLSX, PPT, PPTX documents are allowed.",
    );
  }

  const name = file.name.toLowerCase();
  const mime = (file.type || "").toLowerCase();
  if (
    name.endsWith(".jpg") ||
    name.endsWith(".jpeg") ||
    name.endsWith(".png") ||
    name.endsWith(".webp") ||
    name.endsWith(".heic") ||
    name.endsWith(".heif") ||
    mime === "image/jpeg" ||
    mime === "image/jpg" ||
    mime === "image/png" ||
    mime === "image/webp" ||
    mime === "image/heic" ||
    mime === "image/heif"
  ) {
    return AllowedPostMediaTypes.PostImage;
  }
  if (
    name.endsWith(".mp4") ||
    name.endsWith(".mkv") ||
    name.endsWith(".mov") ||
    name.endsWith(".avi")
  ) {
    return AllowedPostMediaTypes.PostVideo;
  }
  throw new Error(
    "Only jpg, jpeg, png, webp, heic images and mp4, mkv, mov, avi videos are allowed.",
  );
}

export async function OtherMediaUpload(
  file: File,
  onProgress?: (percent: number) => void,
  isDocumentUpload?: boolean,
  /** Optional — omitted by older callers; backend leaves Media.module null. */
  module?: MediaModule,
): Promise<UploadResult> {
  const uploadFile = withResolvedMimeType(file);
  const mediaType = getMediaTypeByExtension(uploadFile, isDocumentUpload);

  if (uploadFile.size > MULTIPART_UPLOAD_THRESHOLD_BYTES) {
    return OtherMediaUploadInChunks(uploadFile, onProgress, isDocumentUpload, module);
  }

  const mutation = isDocumentUpload
    ? `
        mutation UploadDocument($input: UploadPostMediaInput!) {
          uploadDocument(input: $input) {
            fileUrl
          }
        }
      `
    : `
        mutation UploadMedia($input: UploadPostMediaInput!) {
          uploadMedia(input: $input) {
            fileUrl
          }
        }
      `;

  const formData = new FormData();
  formData.append(
    "operations",
    JSON.stringify({
      query: mutation,
      variables: {
        input: {
          media: null,
          mediaType,
          ...(module ? { module } : {}),
        },
      },
    }),
  );
  formData.append(
    "map",
    JSON.stringify({
      "0": ["variables.input.media"],
    }),
  );
  formData.append("0", uploadFile);
  return new Promise((resolve, reject) => {
    const xhr = new XMLHttpRequest();
    xhr.open("POST", BASEURL, true);
    xhr.withCredentials = true;

    xhr.upload.onprogress = (event) => {
      if (event.lengthComputable && onProgress) {
        const percent = Math.round((event.loaded / event.total) * 100);
        onProgress(percent >= 100 ? 99 : percent);
      }
    };

    xhr.onload = () => {
      if (xhr.status >= 200 && xhr.status < 300) {
        try {
          const json = JSON.parse(xhr.responseText);
          if (json.errors && json.errors.length > 0) {
            reject(
              new Error(json.errors[0].message || "Backend error occurred."),
            );
          } else {
            const upload = isDocumentUpload
              ? json?.data?.uploadDocument
              : json?.data?.uploadMedia;
            resolve({
              id: upload?.id ?? null,
              fileUrl: upload?.fileUrl ?? null,
            });
          }
        } catch (e) {
          reject(new Error("Invalid JSON response"));
        }
      } else {
        reject(
          new Error(
            `API Error: ${xhr.status} ${xhr.statusText} - ${xhr.responseText}`,
          ),
        );
      }
    };

    xhr.onerror = () => {
      reject(new Error("Network error"));
    };

    xhr.send(formData);
  });
}

export async function OtherMediaUploadInChunks(
  file: File,
  onProgress?: (percent: number) => void,
  isDocumentUpload?: boolean,
  module?: MediaModule,
): Promise<UploadResult> {
  const mediaType = getMediaTypeByExtension(file, isDocumentUpload);
  const result = await uploadLargeFileInChunks({
    file,
    fileType: mediaType,
    onProgress,
    module,
  });

  return {
    id: result.id ?? null,
    fileUrl: result.fileUrl,
  };
}
